import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ShieldCheck,
  FileText,
  Users,
  MessageSquare,
  Sparkles,
  Star,
  Download,
  Search,
  ChevronDown,
  ChevronRight,
  LogIn,
  AlertTriangle,
  Loader2,
  ArrowUpRight,
  Lock,
} from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import AuthModal from "../components/AuthModal";
import { useAuth } from "../context/AuthContext";
import {
  getAdminStats,
  getAdminUploads,
  getAdminChat,
  getAdminFeedback,
  getAdminUsers,
  downloadCSV,
} from "../lib/admin";
import { formatApiError } from "../lib/authApi";

function StatCard({ icon: Icon, label, value, hint, testId }) {
  return (
    <div
      className="rounded-2xl border border-slate-200 bg-white p-5"
      data-testid={testId}
    >
      <div className="flex items-center gap-3">
        <div className="h-9 w-9 rounded-lg bg-slate-100 text-slate-700 flex items-center justify-center">
          <Icon className="h-4 w-4" />
        </div>
        <div className="text-sm text-slate-500">{label}</div>
      </div>
      <div className="mt-3 font-display text-3xl font-semibold text-slate-900">
        {value === null || value === undefined ? "—" : value}
      </div>
      {hint && <div className="mt-1 text-xs text-slate-400">{hint}</div>}
    </div>
  );
}

function TabButton({ active, onClick, children, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`relative px-4 py-2.5 text-sm font-medium rounded-t-lg -mb-px border-b-2 transition-colors ${
        active
          ? "border-blue-600 text-blue-700"
          : "border-transparent text-slate-500 hover:text-slate-800"
      }`}
    >
      {children}
    </button>
  );
}

function humanDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch (e) {
    return iso.slice(0, 19);
  }
}

const NA = "not specified in your policy";
const cleanNA = (v) => (v && v !== NA ? v : "—");

function UploadsTable({ token }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [insurer, setInsurer] = useState("");
  const [since, setSince] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const params = { limit: 500 };
      if (insurer.trim()) params.insurer = insurer.trim();
      if (since.trim()) params.since = new Date(since).toISOString();
      const d = await getAdminUploads(params);
      setRows(d.items || []);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const exportCsv = () => {
    downloadCSV(
      `kyp_uploads_${new Date().toISOString().slice(0, 10)}.csv`,
      rows,
      [
        "created_at", "id", "filename", "insurer", "vehicle", "policy_number",
        "clarity_score", "analysis_status", "text_source", "size",
        "session_id", "user_id", "ip", "is_deleted",
      ],
    );
  };

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-end gap-3 mb-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            value={insurer}
            onChange={(e) => setInsurer(e.target.value)}
            placeholder="Filter by insurer…"
            data-testid="admin-uploads-search"
            className="w-full pl-9 pr-3 py-2 rounded-full border border-slate-200 text-sm focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400"
          />
        </div>
        <input
          type="date"
          value={since}
          onChange={(e) => setSince(e.target.value)}
          data-testid="admin-uploads-since"
          className="rounded-full border border-slate-200 px-3 py-2 text-sm"
        />
        <button
          onClick={load}
          disabled={loading}
          data-testid="admin-uploads-apply"
          className="rounded-full border border-slate-200 hover:bg-slate-50 px-4 py-2 text-sm font-medium"
        >
          Apply
        </button>
        <button
          onClick={exportCsv}
          disabled={rows.length === 0}
          data-testid="admin-uploads-csv"
          className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 hover:bg-slate-800 disabled:bg-slate-300 text-white px-4 py-2 text-sm font-medium"
        >
          <Download className="h-3.5 w-3.5" />
          Export CSV
        </button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-500 text-sm py-6" data-testid="admin-uploads-loading">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading uploads…
        </div>
      ) : error ? (
        <div className="rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2">{error}</div>
      ) : rows.length === 0 ? (
        <div className="text-slate-500 text-sm py-6">No uploads match.</div>
      ) : (
        <div className="overflow-x-auto -mx-2 md:mx-0">
          <table className="min-w-full text-sm" data-testid="admin-uploads-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <th className="py-2.5 px-3">Date</th>
                <th className="py-2.5 px-3">Insurer</th>
                <th className="py-2.5 px-3">Vehicle</th>
                <th className="py-2.5 px-3">Policy no.</th>
                <th className="py-2.5 px-3">Score</th>
                <th className="py-2.5 px-3">Status</th>
                <th className="py-2.5 px-3">Session / User</th>
                <th className="py-2.5 px-3"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50/60">
                  <td className="py-2.5 px-3 whitespace-nowrap text-slate-600">{humanDate(r.created_at)}</td>
                  <td className="py-2.5 px-3 text-slate-900 max-w-[220px] truncate" title={r.insurer || ""}>{cleanNA(r.insurer)}</td>
                  <td className="py-2.5 px-3 text-slate-700 max-w-[220px] truncate" title={r.vehicle || ""}>{cleanNA(r.vehicle)}</td>
                  <td className="py-2.5 px-3 text-slate-700 max-w-[160px] truncate" title={r.policy_number || ""}>{cleanNA(r.policy_number)}</td>
                  <td className="py-2.5 px-3 tabular-nums font-medium text-slate-900">{r.clarity_score ?? "—"}</td>
                  <td className="py-2.5 px-3">
                    <span
                      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${
                        r.analysis_status === "complete"
                          ? "bg-emerald-50 text-emerald-700"
                          : r.analysis_status === "failed"
                          ? "bg-red-50 text-red-700"
                          : r.analysis_status === "running"
                          ? "bg-blue-50 text-blue-700"
                          : "bg-slate-100 text-slate-600"
                      }`}
                    >
                      {r.analysis_status}
                      {r.is_deleted && <span className="text-slate-400">(deleted)</span>}
                    </span>
                  </td>
                  <td className="py-2.5 px-3 text-xs text-slate-500">
                    <div className="truncate max-w-[160px]" title={r.user_id || r.session_id || ""}>
                      {r.user_id ? "user:" + r.user_id.slice(0, 8) : r.session_id?.slice(0, 12)}
                    </div>
                  </td>
                  <td className="py-2.5 px-3">
                    <Link
                      to={`/policy/${r.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-0.5 text-blue-600 hover:text-blue-700 text-xs"
                    >
                      View
                      <ArrowUpRight className="h-3 w-3" />
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ChatTable() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [uploadFilter, setUploadFilter] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const params = { limit: 500 };
      if (uploadFilter.trim()) params.upload_id = uploadFilter.trim();
      const d = await getAdminChat(params);
      setRows(d.items || []);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const questions = useMemo(() => rows.filter((r) => r.role === "user"), [rows]);

  const exportCsv = () => {
    downloadCSV(
      `kyp_askai_${new Date().toISOString().slice(0, 10)}.csv`,
      questions,
      [
        "created_at", "id", "content", "upload_id", "insurer", "vehicle",
        "user_email", "user_id",
      ],
    );
  };

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-end gap-3 mb-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            value={uploadFilter}
            onChange={(e) => setUploadFilter(e.target.value)}
            placeholder="Filter by upload id…"
            data-testid="admin-chat-search"
            className="w-full pl-9 pr-3 py-2 rounded-full border border-slate-200 text-sm focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400"
          />
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="rounded-full border border-slate-200 hover:bg-slate-50 px-4 py-2 text-sm font-medium"
        >
          Apply
        </button>
        <button
          onClick={exportCsv}
          disabled={questions.length === 0}
          data-testid="admin-chat-csv"
          className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 hover:bg-slate-800 disabled:bg-slate-300 text-white px-4 py-2 text-sm font-medium"
        >
          <Download className="h-3.5 w-3.5" />
          Export CSV
        </button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-500 text-sm py-6">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading Ask AI history…
        </div>
      ) : error ? (
        <div className="rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2">{error}</div>
      ) : questions.length === 0 ? (
        <div className="text-slate-500 text-sm py-6">No questions yet.</div>
      ) : (
        <div className="overflow-x-auto -mx-2 md:mx-0">
          <table className="min-w-full text-sm" data-testid="admin-chat-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <th className="py-2.5 px-3">Asked at</th>
                <th className="py-2.5 px-3">Question</th>
                <th className="py-2.5 px-3">Policy (insurer / vehicle)</th>
                <th className="py-2.5 px-3">User</th>
                <th className="py-2.5 px-3"></th>
              </tr>
            </thead>
            <tbody>
              {questions.map((q) => (
                <tr key={q.id} className="border-b border-slate-100 hover:bg-slate-50/60 align-top">
                  <td className="py-2.5 px-3 whitespace-nowrap text-slate-600">{humanDate(q.created_at)}</td>
                  <td className="py-2.5 px-3 text-slate-900 max-w-[420px]">
                    <div className="line-clamp-3">{q.content}</div>
                  </td>
                  <td className="py-2.5 px-3 text-slate-600 max-w-[220px]">
                    <div className="truncate" title={q.insurer || ""}>{cleanNA(q.insurer)}</div>
                    <div className="truncate text-xs text-slate-500" title={q.vehicle || ""}>{cleanNA(q.vehicle)}</div>
                  </td>
                  <td className="py-2.5 px-3 text-xs text-slate-500 max-w-[180px] truncate" title={q.user_email || q.user_id}>
                    {q.user_email || q.user_id?.slice(0, 8)}
                  </td>
                  <td className="py-2.5 px-3">
                    {q.upload_id && (
                      <Link
                        to={`/policy/${q.upload_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-0.5 text-blue-600 hover:text-blue-700 text-xs"
                      >
                        Policy
                        <ArrowUpRight className="h-3 w-3" />
                      </Link>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function FeedbackTable() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [expanded, setExpanded] = useState(new Set());
  const [recommend, setRecommend] = useState("");
  const [accuracy, setAccuracy] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const params = { limit: 500 };
      if (recommend) params.recommend = recommend;
      if (accuracy) params.accuracy = accuracy;
      const d = await getAdminFeedback(params);
      setRows(d.items || []);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggle = (id) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const exportCsv = () => {
    downloadCSV(
      `kyp_feedback_${new Date().toISOString().slice(0, 10)}.csv`,
      rows,
      [
        "created_at", "ease", "accuracy", "recommend", "what_worked",
        "what_didnt", "wrong_details", "other", "name", "upload_id",
        "insurer", "vehicle", "session_id",
      ],
    );
  };

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-end gap-3 mb-4">
        <select
          value={recommend}
          onChange={(e) => setRecommend(e.target.value)}
          data-testid="admin-feedback-recommend"
          className="rounded-full border border-slate-200 px-3 py-2 text-sm"
        >
          <option value="">Recommend: any</option>
          <option value="yes">Yes</option>
          <option value="maybe">Maybe</option>
          <option value="no">No</option>
        </select>
        <select
          value={accuracy}
          onChange={(e) => setAccuracy(e.target.value)}
          data-testid="admin-feedback-accuracy"
          className="rounded-full border border-slate-200 px-3 py-2 text-sm"
        >
          <option value="">Accuracy: any</option>
          <option value="spot_on">Spot on</option>
          <option value="mostly_right">Mostly right</option>
          <option value="some_errors">Some errors</option>
          <option value="way_off">Way off</option>
          <option value="didnt_check">Didn&apos;t check</option>
        </select>
        <button
          onClick={load}
          disabled={loading}
          data-testid="admin-feedback-apply"
          className="rounded-full border border-slate-200 hover:bg-slate-50 px-4 py-2 text-sm font-medium"
        >
          Apply
        </button>
        <div className="md:ml-auto">
          <button
            onClick={exportCsv}
            disabled={rows.length === 0}
            data-testid="admin-feedback-csv"
            className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 hover:bg-slate-800 disabled:bg-slate-300 text-white px-4 py-2 text-sm font-medium"
          >
            <Download className="h-3.5 w-3.5" />
            Export CSV
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-500 text-sm py-6">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading feedback…
        </div>
      ) : error ? (
        <div className="rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2">{error}</div>
      ) : rows.length === 0 ? (
        <div className="text-slate-500 text-sm py-6">No feedback yet.</div>
      ) : (
        <div className="space-y-2" data-testid="admin-feedback-list">
          {rows.map((f) => {
            const open = expanded.has(f.id);
            const recCls =
              f.recommend === "yes"
                ? "bg-emerald-50 text-emerald-700"
                : f.recommend === "no"
                ? "bg-red-50 text-red-700"
                : "bg-amber-50 text-amber-700";
            return (
              <div
                key={f.id}
                className="rounded-xl border border-slate-200 bg-white"
                data-testid={`admin-feedback-item-${f.id}`}
              >
                <button
                  type="button"
                  onClick={() => toggle(f.id)}
                  className="w-full flex items-start gap-3 p-3 text-left hover:bg-slate-50/60"
                >
                  {open ? (
                    <ChevronDown className="h-4 w-4 mt-1 text-slate-400" />
                  ) : (
                    <ChevronRight className="h-4 w-4 mt-1 text-slate-400" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap gap-1.5 items-center">
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${recCls}`}>
                        recommend · {f.recommend}
                      </span>
                      <span className="inline-flex rounded-full bg-slate-100 text-slate-700 px-2 py-0.5 text-[10px] font-medium">
                        accuracy · {f.accuracy}
                      </span>
                      <span className="inline-flex rounded-full bg-slate-100 text-slate-700 px-2 py-0.5 text-[10px] font-medium">
                        ease · {f.ease}
                      </span>
                      {f.upload_id && (
                        <span className="inline-flex rounded-full bg-blue-50 text-blue-700 px-2 py-0.5 text-[10px] font-medium">
                          policy · {cleanNA(f.insurer) || f.upload_id.slice(0, 8)}
                        </span>
                      )}
                    </div>
                    <div className="mt-1.5 text-sm text-slate-900">
                      {f.name ? <strong>{f.name}</strong> : <span className="text-slate-500 italic">Anonymous</span>}
                      <span className="text-slate-400 text-xs ml-2">· {humanDate(f.created_at)}</span>
                    </div>
                    {(f.other || f.wrong_details) && !open && (
                      <div className="mt-1 text-sm text-slate-600 line-clamp-1">
                        {f.other || f.wrong_details}
                      </div>
                    )}
                  </div>
                </button>
                {open && (
                  <div className="border-t border-slate-100 p-4 space-y-3 text-sm bg-slate-50/40">
                    {f.what_worked?.length > 0 && (
                      <div>
                        <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">What worked</div>
                        <div className="flex flex-wrap gap-1.5">
                          {f.what_worked.map((w) => (
                            <span key={w} className="rounded-full bg-emerald-50 text-emerald-700 px-2 py-0.5 text-xs font-medium">
                              {w.replace(/_/g, " ")}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {f.what_didnt?.length > 0 && (
                      <div>
                        <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">What didn&apos;t</div>
                        <div className="flex flex-wrap gap-1.5">
                          {f.what_didnt.map((w) => (
                            <span key={w} className="rounded-full bg-red-50 text-red-700 px-2 py-0.5 text-xs font-medium">
                              {w.replace(/_/g, " ")}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {f.wrong_details && (
                      <div>
                        <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Wrong details</div>
                        <p className="text-slate-800 whitespace-pre-wrap">{f.wrong_details}</p>
                      </div>
                    )}
                    {f.other && (
                      <div>
                        <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Anything else</div>
                        <p className="text-slate-800 whitespace-pre-wrap">{f.other}</p>
                      </div>
                    )}
                    <div className="pt-2 border-t border-slate-200 flex flex-wrap gap-3 text-xs text-slate-500">
                      <span>id: <code>{f.id}</code></span>
                      {f.session_id && <span>session: <code>{f.session_id.slice(0, 12)}…</code></span>}
                      {f.upload_id && (
                        <Link
                          to={`/policy/${f.upload_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-0.5 text-blue-600 hover:text-blue-700"
                        >
                          Open policy
                          <ArrowUpRight className="h-3 w-3" />
                        </Link>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function UsersTable() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const d = await getAdminUsers({ limit: 500 });
      setRows(d.items || []);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const exportCsv = () =>
    downloadCSV(
      `kyp_users_${new Date().toISOString().slice(0, 10)}.csv`,
      rows,
      ["created_at", "id", "email", "name", "provider"],
    );

  return (
    <div>
      <div className="flex justify-end mb-4">
        <button
          onClick={exportCsv}
          disabled={rows.length === 0}
          data-testid="admin-users-csv"
          className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 hover:bg-slate-800 disabled:bg-slate-300 text-white px-4 py-2 text-sm font-medium"
        >
          <Download className="h-3.5 w-3.5" />
          Export CSV
        </button>
      </div>
      {loading ? (
        <div className="flex items-center gap-2 text-slate-500 text-sm py-6">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading users…
        </div>
      ) : error ? (
        <div className="rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2">{error}</div>
      ) : (
        <div className="overflow-x-auto -mx-2 md:mx-0">
          <table className="min-w-full text-sm" data-testid="admin-users-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <th className="py-2.5 px-3">Registered</th>
                <th className="py-2.5 px-3">Email</th>
                <th className="py-2.5 px-3">Name</th>
                <th className="py-2.5 px-3">Provider</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((u) => (
                <tr key={u.id} className="border-b border-slate-100">
                  <td className="py-2.5 px-3 whitespace-nowrap text-slate-600">{humanDate(u.created_at)}</td>
                  <td className="py-2.5 px-3 text-slate-900">{u.email}</td>
                  <td className="py-2.5 px-3 text-slate-700">{u.name || "—"}</td>
                  <td className="py-2.5 px-3 text-slate-500">{u.provider || "email"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function AdminPanel() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [authOpen, setAuthOpen] = useState(false);
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [statsError, setStatsError] = useState("");
  const [statsLoading, setStatsLoading] = useState(false);

  const isAdmin = !!user?.is_admin;

  useEffect(() => {
    if (!authLoading && !user) setAuthOpen(true);
  }, [authLoading, user]);

  useEffect(() => {
    if (!isAdmin) return;
    setStatsLoading(true);
    getAdminStats()
      .then((d) => {
        setStats(d);
        setStatsError("");
      })
      .catch((e) => setStatsError(formatApiError(e)))
      .finally(() => setStatsLoading(false));
  }, [isAdmin]);

  return (
    <div className="min-h-screen bg-slate-50/40">
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 md:pt-10 pb-12">
        {/* Header */}
        <div className="flex items-center gap-3 mb-2">
          <div className="h-9 w-9 rounded-lg bg-slate-900 text-white flex items-center justify-center">
            <ShieldCheck className="h-5 w-5" />
          </div>
          <h1 className="font-display text-3xl md:text-4xl tracking-tight text-slate-900">
            Admin Panel
          </h1>
        </div>
        <p className="text-slate-500 max-w-2xl">
          Owner-only. Overview of everything happening on KnowYourPolicy.ai.
        </p>

        {authLoading ? (
          <div className="mt-10 flex items-center gap-2 text-slate-500 text-sm">
            <Loader2 className="h-4 w-4 animate-spin" />
            Checking access…
          </div>
        ) : !user ? (
          <div
            className="mt-8 rounded-2xl border border-amber-200 bg-amber-50 p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4"
            data-testid="admin-signed-out"
          >
            <div className="h-11 w-11 rounded-xl bg-amber-500 text-white flex items-center justify-center shrink-0">
              <Lock className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <h2 className="font-display text-lg font-semibold text-slate-900">
                Log in to continue
              </h2>
              <p className="text-sm text-slate-600 mt-0.5">
                This area is restricted to the site owner.
              </p>
            </div>
            <button
              onClick={() => setAuthOpen(true)}
              data-testid="admin-login-btn"
              className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-full px-5 py-2.5 text-sm font-semibold"
            >
              <LogIn className="h-4 w-4" />
              Log in
            </button>
          </div>
        ) : !isAdmin ? (
          <div
            className="mt-8 rounded-2xl border border-red-200 bg-red-50 p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4"
            data-testid="admin-forbidden"
          >
            <div className="h-11 w-11 rounded-xl bg-red-500 text-white flex items-center justify-center shrink-0">
              <AlertTriangle className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <h2 className="font-display text-lg font-semibold text-red-900">
                Not authorized
              </h2>
              <p className="text-sm text-red-800/90 mt-0.5">
                Your account (<code>{user.email}</code>) doesn&apos;t have owner
                access. If this is a mistake, contact the site owner.
              </p>
            </div>
            <button
              onClick={() => navigate("/")}
              className="rounded-full border border-red-200 hover:bg-red-100 text-red-700 px-4 py-2 text-sm font-medium"
              data-testid="admin-forbidden-home"
            >
              Go home
            </button>
          </div>
        ) : (
          <>
            {/* Tabs */}
            <div className="mt-8 border-b border-slate-200 flex flex-wrap gap-1" data-testid="admin-tabs">
              <TabButton active={tab === "overview"} onClick={() => setTab("overview")} testId="admin-tab-overview">
                Overview
              </TabButton>
              <TabButton active={tab === "uploads"} onClick={() => setTab("uploads")} testId="admin-tab-uploads">
                Policies
              </TabButton>
              <TabButton active={tab === "chat"} onClick={() => setTab("chat")} testId="admin-tab-chat">
                Ask AI
              </TabButton>
              <TabButton active={tab === "feedback"} onClick={() => setTab("feedback")} testId="admin-tab-feedback">
                Feedback
              </TabButton>
              <TabButton active={tab === "users"} onClick={() => setTab("users")} testId="admin-tab-users">
                Users
              </TabButton>
            </div>

            <div className="mt-6" data-testid="admin-tab-content">
              {tab === "overview" && (
                <div>
                  {statsLoading ? (
                    <div className="flex items-center gap-2 text-slate-500 text-sm py-6">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Loading stats…
                    </div>
                  ) : statsError ? (
                    <div className="rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2">
                      {statsError}
                    </div>
                  ) : (
                    <>
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                        <StatCard
                          icon={FileText}
                          label="Policies uploaded"
                          value={stats?.total_uploads_active}
                          hint={`${stats?.total_uploads_all_time ?? 0} lifetime`}
                          testId="stat-uploads"
                        />
                        <StatCard
                          icon={Users}
                          label="Registered users"
                          value={stats?.total_users}
                          testId="stat-users"
                        />
                        <StatCard
                          icon={Sparkles}
                          label="Analyses complete"
                          value={stats?.total_analyses_complete}
                          hint={`${stats?.total_analyses_all ?? 0} attempted`}
                          testId="stat-analyses"
                        />
                        <StatCard
                          icon={Star}
                          label="Avg KYP Score"
                          value={stats?.avg_clarity_score}
                          hint={`n=${stats?.scored_analyses_count ?? 0}`}
                          testId="stat-avg-score"
                        />
                        <StatCard
                          icon={MessageSquare}
                          label="Ask AI questions"
                          value={stats?.total_ask_ai_questions}
                          testId="stat-questions"
                        />
                        <StatCard
                          icon={MessageSquare}
                          label="Feedback submissions"
                          value={stats?.total_feedback}
                          testId="stat-feedback"
                        />
                        <StatCard
                          icon={ShieldCheck}
                          label="Consent records"
                          value={stats?.total_consents}
                          testId="stat-consents"
                        />
                      </div>

                      {/* Recommend distribution */}
                      <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-6" data-testid="stat-recommend-dist">
                        <div className="text-xs uppercase tracking-widest text-slate-500 font-medium">
                          Would-recommend distribution
                        </div>
                        <div className="mt-4 grid grid-cols-3 gap-3">
                          {[
                            ["yes", "Yes", "bg-emerald-500"],
                            ["maybe", "Maybe", "bg-amber-500"],
                            ["no", "No", "bg-red-500"],
                          ].map(([k, label, color]) => {
                            const n = stats?.recommend_distribution?.[k] ?? 0;
                            const total = (stats?.total_feedback || 1);
                            const pct = total ? Math.round((n / total) * 100) : 0;
                            return (
                              <div key={k} className="rounded-xl bg-slate-50 border border-slate-200 p-4">
                                <div className="text-sm text-slate-500">{label}</div>
                                <div className="mt-1 font-display text-2xl font-semibold text-slate-900">
                                  {n} <span className="text-sm text-slate-400 font-normal">({pct}%)</span>
                                </div>
                                <div className="mt-2 h-1.5 rounded-full bg-slate-200 overflow-hidden">
                                  <div className={`h-full ${color}`} style={{ width: `${pct}%` }} />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}
              {tab === "uploads" && <UploadsTable />}
              {tab === "chat" && <ChatTable />}
              {tab === "feedback" && <FeedbackTable />}
              {tab === "users" && <UsersTable />}
            </div>
          </>
        )}
      </div>

      <Footer />
      <AuthModal
        open={authOpen}
        onClose={() => {
          setAuthOpen(false);
          if (!user) navigate("/");
        }}
        onSuccess={() => setAuthOpen(false)}
        headline="Owner sign-in"
        subheadline="Log in with your admin email to access the admin panel."
      />
    </div>
  );
}
